/**
 * Per-game Penelope control hook — the data half of the control on
 * /live/:gameKey. Penelope is the per-trader Pinnacle maker bot
 * (relay/src/services/penelope). A game is shadow by default (decides and
 * simulates, never writes to Kalshi); its baseline goes live only when the box
 * allows it and the trader holds the live grant.
 *
 * One read: GET /api/penelope/games/by-event/:eventTicker returns the caller's
 * own game for this event (or null) and whether one can be attached. Every
 * mutation is a relay desired-state write the conductor enacts within a second;
 * the relay re-checks the grant on each one.
 */
import { useCallback, useMemo, useState } from 'react';
import { apiFetch } from '@/stores/authStore';
import { eventTickerOfMarket } from '@/lib/vanilla/slate';
import type { GameRow } from '@/views/ValueDashboard/types';
import { larryLegsOf } from '@/views/Cockpit/larryMatch';
import { useVisibilityAwarePoll } from './useVisibilityAwarePoll';

const POLL_MS = 2_000;

export type PenelopeLean = 'neutral' | 'a' | 'b';
/** One resting order of hers. `orderId` / `why` are absent from relays before PEN-01 (2026-10-10). */
export interface PenelopeRest { px: number; size: number; orderId?: string; why?: string }
export type PenelopeRegime = 'IDLE' | 'VALUE' | 'PULL' | 'PAIR' | 'HOLD' | 'PAUSED' | 'STOPPED' | 'HARVEST';
export type PenelopeLiquidityGatePolicy = 'pause' | 'hold' | 'quote';
/** A researcher's own variant: the game's config with `overlay` applied, run beside the baseline in simulation. */
export interface PenelopeVariantSpec { name: string; overlay: Record<string, unknown>; legs?: 'noNo' | 'yesYes' | 'oneMarket' }
/** A built-in variant by name, or a custom spec. */
export type PenelopeVariant = string | PenelopeVariantSpec;
export const isVariantSpec = (v: PenelopeVariant): v is PenelopeVariantSpec => typeof v === 'object' && v !== null;
export const variantName = (v: PenelopeVariant): string => (isVariantSpec(v) ? v.name : v);

/** The knobs the control edits. The relay holds more (see PenelopeGameConfig); unknown keys ride along untouched. */
export interface PenelopeConfig {
  enabled: boolean;
  mode: 'shadow' | 'live';
  lean: PenelopeLean;
  clipContracts: number;
  /** C02-F02: the clip of a Pinnacle-qualified sell-back; 0 / absent = Contract Size (relay ≥ 2026-10-15). */
  sellBackClipContracts?: number;
  /** Doom 10-16: a short-side entry sized by Contract Size (default) or by the Sell-back Contract Size. */
  shortEntryClip?: 'contract' | 'sellBack';
  maxNetContracts: number;
  leanMaxNetContracts: number;
  maxGrossContracts: number;
  /** PEN-05 entry band (relay default 3 / 97, inclusive): an adding order only at a price within it. */
  bidFloorCents?: number;
  bidCeilingCents?: number;
  minEdgeCents: number;
  cancelEdgeCents: number;
  pairEnabled: boolean;
  pairLockCents: number;
  pullPolicy: 'cancelAll' | 'cancelAdding' | 'hold' | 'widen';
  /** What may take a resting order off (relay default stay); pullPolicy applies under chase only. */
  restPolicy?: 'stay' | 'chase';
  /** Volatility harvester rows: depth (contracts, top 4 levels) a filled side waits for, and for how long. */
  harvestRecoverDepth?: number;
  harvestRecoverPersistMs?: number;
  /** PEN-01: may she BUY team A (away) / team B (home)? Absent = armed (older games). */
  armA?: boolean;
  armB?: boolean;
  touchPolicy?: 'top' | 'legacy';
  /** What an adding side does while Pinnacle is off (relay default follow = PEN-02). */
  offLinePolicy?: 'follow' | 'pair';
  /** false = no sell-back rests while Pinnacle is off (Doom 10-14). */
  sellBackWhileOff?: boolean;
  followMaxAboveCents?: number;
  followRefMaxAgeMs?: number;
  followEdgeGuard?: boolean;
  touchMinContracts?: number;
  valueConfirmMs?: number;
  /** Split book (Doom R3, 2026-10-16): both top bids posted when each shows value under the ceiling; relay default off / 25 / 1¢ / cancel. */
  splitBookEnabled?: boolean;
  splitClipContracts?: number;
  splitMaxEdgeCents?: number;
  splitOnLiquidityDrop?: 'cancel' | 'keep';
  splitWhileOff?: 'cancel' | 'keep';
  /** Doom 10-16: a new bracket needs the completing side placeable (default on); no completion in overtime (NHL default on). */
  pairNeedsBothSides?: boolean;
  pairNoCompleteInOT?: boolean;
  edgeGraceMs?: number;
  addMaxPerMin?: number;
  restEdgeMaxAgeMs?: number;
  liquidityGatePolicy?: PenelopeLiquidityGatePolicy;
  variants: PenelopeVariant[];
  [k: string]: unknown;
}

interface Holding { contracts: number; costCents: number }
export interface PenelopeLive {
  /** false = the baseline is placing real orders on Kalshi (the named variants stay simulated). */
  shadow: boolean;
  gameKey: string | null;
  phase: { phase: 'pre' | 'live' | 'final' | 'unknown'; reason: string | null };
  baseline: {
    regime: PenelopeRegime;
    gate: string | null;
    stopReason: string | null;
    /** Pinnacle fair per team in cents; A = ticker A's YES team (the away side as attached here). */
    fairC: { A: number | null; B: number | null };
    pinnacleState: 'present' | 'absent' | 'withdrawn' | null;
    fairBlock: string | null;
    vendorAgeMs: number | null;
    /** Relay ≥ 2026-10-15: the line is past max Pinnacle age but kept as still connected. */
    fairStill?: { ageMs: number; driftCents: number | null } | null;
    inv: { A: Holding; B: Holding };
    net: number;
    pnlCents: number;
    /** What rests per side, its venue order and why it was placed (value / unwind / pair: bracket …). */
    rests: { A: PenelopeRest | null; B: PenelopeRest | null };
    /** What each side wants and why, as of the last pass — the reason a side is NOT posting (relay ≥ 10-14). */
    desiredWhy?: { A: string | null; B: string | null };
    /** Follow reference per side: the level Pinnacle last called good value, and how long ago. */
    ref?: { A: { px: number; ageMs: number } | null; B: { px: number; ageMs: number } | null };
    /** Age of Pinnacle's last line while it is off. */
    lastFairAgeMs?: number | null;
    /** The unpaired tail she is selling back (which team, how many, what they cost). */
    tail?: { team: 'A' | 'B'; contracts: number; avgCents: number } | null;
    places: number;
    cancels: number;
  };
  variants: Array<{ name: string; regime: PenelopeRegime; net: number; pnlCents: number }>;
}

export interface PenelopeGame {
  id: string;
  eventTicker: string;
  tickerA: string;
  tickerB: string;
  status: string;
  stopReason: string | null;
  config: PenelopeConfig;
  fillsCount: number;
  live: PenelopeLive | null;
  /** Armed but not running, and why. */
  parked: string | null;
}

export interface PenelopeControl {
  game: PenelopeGame | null;
  /** A game can be attached here (supported series, and the caller holds the grant). */
  eligible: boolean;
  reasons: string[];
  /** The relay service is up (PENELOPE=1). */
  serviceRunning: boolean;
  /** May a game of the caller's run LIVE here (box flag AND not locked to practice)? */
  liveEligible: boolean;
  /** Why not, in the trader's words; null when eligible. */
  liveReason: string | null;
  lastStopped: PenelopeLastStopped | null;
  /** Box and per-trader game slots, for the "N of M in use" line; null until read. */
  slots: { running: number; maxArmed: number; mine: number; maxMine: number } | null;
  error: string | null;
  busy: boolean;
  /** Attach disarmed, with `config` as the starting settings (else the relay's defaults). */
  attach: (config?: Partial<PenelopeConfig>) => Promise<void>;
  /** One click: attach this game if it is not attached yet (with `config` as its starting settings), then arm it. */
  run: (config?: Partial<PenelopeConfig>) => Promise<void>;
  patch: (config: Partial<PenelopeConfig>) => Promise<void>;
  setArm: (armed: boolean) => Promise<void>;
  kill: () => Promise<void>;
  remove: () => Promise<void>;
  /** VH-01: the caller's volatility harvester on this event (its own row, its own controls). Absent in old fixtures. */
  harvester?: PenelopeHarvesterControl;
}

/** The volatility harvester's row and controls (relay strategy 'harvester'; practice only unless the box allows live). */
export interface PenelopeHarvesterControl {
  game: PenelopeGame | null;
  liveEligible: boolean;
  /** Attach (strategy 'harvester', with `config` over the relay's harvester start) if needed, then arm. */
  run: (config?: Partial<PenelopeConfig>) => Promise<void>;
  patch: (config: Partial<PenelopeConfig>) => Promise<void>;
  setArm: (armed: boolean) => Promise<void>;
  remove: () => Promise<void>;
}

/** A game of the caller's that stopped on its own recently (the ticket's "Stopped: <reason>" banner). */
export interface PenelopeLastStopped { stopReason: string | null; at: string; mode: 'live' | 'shadow' }

interface ByEventResponse {
  game: PenelopeGame | null;
  harvester?: PenelopeGame | null;
  harvesterLiveEligible?: boolean;
  eligible: boolean;
  reasons: string[];
  /** Box allows live AND the caller is not locked to practice. */
  liveEligible?: boolean;
  advancedEligible?: boolean;
  lastStopped?: PenelopeLastStopped | null;
  /** The caller's armed games on this box (against health.maxArmedPerTrader). */
  armedByCaller?: number;
  health: { running: boolean; enabled?: boolean; liveAllowed?: boolean; runners?: number; maxArmed?: number; maxArmedPerTrader?: number };
}

/** The Kalshi event a game's Penelope row is keyed by (null when the game has no sibling moneyline legs). */
export function penelopeEventTicker(game: GameRow): string | null {
  const legs = larryLegsOf(game);
  return legs ? eventTickerOfMarket(legs.tickerA) : null;
}

/** What attach and one-click Start send for a game: ticker A is the AWAY market and ticker B the HOME market, so
 *  "team A" is always the away side. Null when the game has no sibling moneyline legs. */
export function penelopeAttachInput(game: GameRow): { eventTicker: string; tickerA: string; tickerB: string; labelA: string; labelB: string; gameKey: string } | null {
  const legs = larryLegsOf(game);
  if (!legs) return null;
  return { eventTicker: eventTickerOfMarket(legs.tickerA), tickerA: legs.tickerA, tickerB: legs.tickerB, labelA: game.awayCode, labelB: game.homeCode, gameKey: game.key };
}

/** `enabled` false polls nothing: the caller has no grant, so there is nothing it may read. */
export function usePenelope(game: GameRow | null, enabled: boolean): PenelopeControl {
  const [data, setData] = useState<ByEventResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Ticker A is the AWAY market and ticker B the HOME market, so "team A" is always the away side on this screen.
  const attachInput = useMemo(() => (game ? penelopeAttachInput(game) : null), [game]);
  const eventTicker = attachInput?.eventTicker ?? null;

  const read = useCallback(
    async (signal: AbortSignal) => {
      if (!eventTicker) return;
      try {
        const res = await apiFetch(`/api/penelope/games/by-event/${encodeURIComponent(eventTicker)}`);
        const body = (await res.json().catch(() => ({}))) as ByEventResponse & { error?: string };
        if (signal.aborted) return;
        if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
        setData(body);
        setError(null);
      } catch (e) {
        if (!signal.aborted) setError((e as Error).message);
      }
    },
    [eventTicker],
  );
  const { refresh } = useVisibilityAwarePoll(read, POLL_MS, { enabled: enabled && eventTicker !== null });

  const call = useCallback(
    async (path: string, method: string, body?: unknown) => {
      setBusy(true);
      try {
        const res = await apiFetch(path, {
          method,
          headers: { 'content-type': 'application/json' },
          body: body === undefined ? undefined : JSON.stringify(body),
        });
        if (!res.ok) throw new Error(((await res.json().catch(() => ({}))) as { error?: string }).error || `HTTP ${res.status}`);
        setError(null);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        // Busy until the re-read lands: a switch clicked in between would write the draft (the row still reads as
        // unattached) and the next poll would throw the click away.
        try { await refresh(); } finally { setBusy(false); }
      }
    },
    [refresh],
  );

  const id = data?.game?.id ?? null;
  const hid = data?.harvester?.id ?? null;
  const harvesterRun = useCallback(async (config?: Partial<PenelopeConfig>) => {
    if (!attachInput) return;
    setBusy(true);
    try {
      const post = async (path: string, body?: unknown): Promise<{ game?: { id?: string } }> => {
        const res = await apiFetch(path, { method: 'POST', headers: { 'content-type': 'application/json' }, body: body === undefined ? undefined : JSON.stringify(body) });
        const json = (await res.json().catch(() => ({}))) as { error?: string; game?: { id?: string } };
        if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
        return json;
      };
      const gameId = hid ?? (await post('/api/penelope/games', { ...attachInput, strategy: 'harvester', ...(config ? { config } : {}) })).game?.id ?? null;
      if (gameId) await post(`/api/penelope/games/${gameId}/arm`);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
      await refresh();
    }
  }, [attachInput, hid, refresh]);
  const harvesterPatch = useCallback(async (config: Partial<PenelopeConfig>) => { if (hid) await call(`/api/penelope/games/${hid}`, 'PATCH', { config }); }, [hid, call]);
  const harvesterSetArm = useCallback(async (armed: boolean) => { if (hid) await call(`/api/penelope/games/${hid}/${armed ? 'arm' : 'disarm'}`, 'POST'); }, [hid, call]);
  const harvesterRemove = useCallback(async () => { if (hid) await call(`/api/penelope/games/${hid}`, 'DELETE'); }, [hid, call]);
  const harvester = useMemo<PenelopeHarvesterControl>(() => ({
    game: data?.harvester ?? null, liveEligible: data?.harvesterLiveEligible === true,
    run: harvesterRun, patch: harvesterPatch, setArm: harvesterSetArm, remove: harvesterRemove,
  }), [data?.harvester, data?.harvesterLiveEligible, harvesterRun, harvesterPatch, harvesterSetArm, harvesterRemove]);
  return {
    harvester,
    game: data?.game ?? null,
    eligible: data?.eligible ?? false,
    reasons: data?.reasons ?? [],
    serviceRunning: data?.health.running ?? false,
    liveEligible: data?.liveEligible === true,
    liveReason: !data || data.liveEligible === true ? null
      : data.health.liveAllowed === false ? 'live orders are off on this box'
        : 'a manager set you to practice (shadow) only',
    lastStopped: data?.lastStopped ?? null,
    slots: data && typeof data.health.maxArmed === 'number' && typeof data.health.runners === 'number'
      ? { running: data.health.runners, maxArmed: data.health.maxArmed, mine: data.armedByCaller ?? 0, maxMine: data.health.maxArmedPerTrader ?? Number.POSITIVE_INFINITY }
      : null,
    error,
    busy,
    attach: useCallback(async (config) => { if (attachInput) await call('/api/penelope/games', 'POST', config ? { ...attachInput, config } : attachInput); }, [attachInput, call]),
    run: useCallback(async (config) => {
      if (!attachInput) return;
      setBusy(true);
      try {
        const post = async (path: string, body?: unknown): Promise<{ game?: { id?: string } }> => {
          const res = await apiFetch(path, { method: 'POST', headers: { 'content-type': 'application/json' }, body: body === undefined ? undefined : JSON.stringify(body) });
          const json = (await res.json().catch(() => ({}))) as { error?: string; game?: { id?: string } };
          if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
          return json;
        };
        // Attach is born disarmed on the relay; arming is its own call, so a failed arm leaves an attached, idle game.
        const gameId = id ?? (await post('/api/penelope/games', config ? { ...attachInput, config } : attachInput)).game?.id ?? null;
        if (gameId) await post(`/api/penelope/games/${gameId}/arm`);
        setError(null);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        try { await refresh(); } finally { setBusy(false); }
      }
    }, [attachInput, id, refresh]),
    patch: useCallback(async (config) => { if (id) await call(`/api/penelope/games/${id}`, 'PATCH', { config }); }, [id, call]),
    setArm: useCallback(async (armed) => { if (id) await call(`/api/penelope/games/${id}/${armed ? 'arm' : 'disarm'}`, 'POST'); }, [id, call]),
    kill: useCallback(async () => { if (id) await call(`/api/penelope/games/${id}/kill`, 'POST', { confirm: true }); }, [id, call]),
    remove: useCallback(async () => { if (id) await call(`/api/penelope/games/${id}`, 'DELETE'); }, [id, call]),
  };
}
